// Collection Component
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { ShopContext } from '../context/shopcontext';
import { assets } from '../assets/assets';
import Title from '../components/Title';
import SearchBar from '../components/SearchBar';
import ProductItem from '../components/ProductItem';
import axios from 'axios';
import Spinner from '../components/Spinner';
import { useLayoutEffect } from 'react';
import ProductItemSkeleton from '../components/ProductItemSkeleton';
import ScrollButton from '../components/ScrollButton';

const Collection = () => {
  const getInitialProducts = () => {
    const currentPage = sessionStorage.getItem('currentPage') || 1;
    const categoryFilter = sessionStorage.getItem('categoryFilter') || '[]';
    const subCategoryFilter = sessionStorage.getItem('subCategoryFilter') || '[]';
    const sortType = sessionStorage.getItem('sortType') || 'relevant';
    const search = sessionStorage.getItem('search') || '';

    const cacheKey = `products_page_${currentPage}_${categoryFilter}_${subCategoryFilter}_${sortType}_${search}`;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return parsed.products || [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const getInitialTotalPages = () => {
    const currentPage = sessionStorage.getItem('currentPage') || 1;
    const categoryFilter = sessionStorage.getItem('categoryFilter') || '[]';
    const subCategoryFilter = sessionStorage.getItem('subCategoryFilter') || '[]';
    const sortType = sessionStorage.getItem('sortType') || 'relevant';
    const search = sessionStorage.getItem('search') || '';

    const cacheKey = `products_page_${currentPage}_${categoryFilter}_${subCategoryFilter}_${sortType}_${search}`;

    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        return parsed.totalPages || 1;
      } catch {
        return 1;
      }
    }
    return 1;
  };

  const { search, showSearch, backendUrl } = useContext(ShopContext);
  const [products, setProducts] = useState(getInitialProducts());
  const inMemoryCache = {}; // ✅ Instant return cache
  let lastScrollPosition = 0; // ✅ Save scroll position
  const paginationRef = useRef(null);
  const [totalPages, setTotalPages] = useState(getInitialTotalPages());

  const [isLoading, setIsLoading] = useState(false);

  const [showFilter, setShowFilter] = useState(false);
  const [filterProducts, setFilterProducts] = useState([]);
  const [category, setCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [sortType, setSortType] = useState('relevant');
  const [filtersReady, setFiltersReady] = useState(false);
  const [shuffledProducts, setShuffledProducts] = useState([]);

  // get 'sub'

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 60;
  const previousPath = useRef('');
  const location = useLocation();
  const params = useParams();
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const selectedCategory = params.categorySlug || queryParams.get('category');
  const selectedSubCategory = queryParams.get('sub');
const commonSubCategories = ['Antique', 'Temple', 'Kundan', 'AD', 'Kemp', 'Pearl'].sort();

const categorySubCategoryMap = {
  Necklace: [...commonSubCategories],
  Bangles: [...commonSubCategories],
  Harams: [...commonSubCategories],
  Earrings: [...commonSubCategories],
  "Zarviya Specials": [...commonSubCategories],
};
  useEffect(() => {
    if (selectedCategory) {
      setCategory([selectedCategory]);
    }
    if (selectedSubCategory) {
      setSubCategory([selectedSubCategory]);
    }
  }, [selectedCategory, selectedSubCategory]);

  // ✅ 3. Filter products as soon as we have shuffled ones ready
  useEffect(() => {
    if (products.length > 0 && filtersReady) {
      applyFilter(false);
    }
  }, [products, category, subCategory, sortType, search, filtersReady]);

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const urlCategory = params.categorySlug || queryParams.get('category');
    const urlSubCategory = queryParams.get('sub');

    const lastCategory = sessionStorage.getItem('lastCategory');
    const isCategoryChanged = urlCategory && urlCategory !== lastCategory;

    if (isCategoryChanged) {
      sessionStorage.setItem('categoryFilter', JSON.stringify([urlCategory]));
      sessionStorage.removeItem('subCategoryFilter');
      sessionStorage.setItem('lastCategory', urlCategory);
      sessionStorage.removeItem('currentPage');
      sessionStorage.removeItem("'collectionScrollPosition'");
    }

    const finalCategory = safeJSONParse(sessionStorage.getItem('categoryFilter'));
    const finalSubCategory = safeJSONParse(sessionStorage.getItem('subCategoryFilter'));
    const savedSortType = sessionStorage.getItem('sortType');
    const savedPage = sessionStorage.getItem('currentPage');
    const savedScroll = sessionStorage.getItem("'collectionScrollPosition'");

    if (finalCategory.length > 0) setCategory(finalCategory);
    if (finalSubCategory.length > 0) setSubCategory(finalSubCategory);
    if (savedSortType) setSortType(savedSortType);
    if (savedPage) setCurrentPage(parseInt(savedPage, 10));

    previousPath.current = currentPath;
    setFiltersReady(true);
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    const savedPosition = sessionStorage.getItem('collectionScrollPosition');

    requestAnimationFrame(() => {
      if (savedPosition) {
        window.scrollTo(0, parseInt(savedPosition, 10));

        document.body.style.pointerEvents = 'none';
        setTimeout(() => {
          document.body.style.pointerEvents = '';
        }, 50);

        setTimeout(() => {
          sessionStorage.removeItem('collectionScrollPosition');
        }, 250);
      } else {
        // ✅ If no saved position, go to top immediately
        window.scrollTo(0, 0);
      }
    });
  }, []);

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const categoryAwareShuffle = (array) => {
    if (array.length === 0) return [];

    // Step 1: Group products by category
    const categoryGroups = {};
    array.forEach((item) => {
      const key = item.category || 'Other';
      if (!categoryGroups[key]) categoryGroups[key] = [];
      categoryGroups[key].push(item);
    });

    const categories = Object.keys(categoryGroups);
    const maxGroupSize = Math.max(...categories.map((cat) => categoryGroups[cat].length));

    const result = [];

    // Step 2: Interleave items from each category
    for (let i = 0; i < maxGroupSize; i++) {
      categories.forEach((cat) => {
        if (categoryGroups[cat][i]) {
          result.push(categoryGroups[cat][i]);
        }
      });
    }

    // Step 3: Optional light shuffle inside the final result for freshness
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }

    return result;
  };

  const softShuffleArray = (array) => {
    const shuffled = [...array];
    const swapTimes = Math.floor(array.length * 3); // Increased shuffle intensity ✅

    for (let i = 0; i < swapTimes; i++) {
      const idx1 = Math.floor(Math.random() * array.length);
      const idx2 = Math.floor(Math.random() * array.length);
      [shuffled[idx1], shuffled[idx2]] = [shuffled[idx2], shuffled[idx1]];
    }

    return shuffled;
  };

  const fetchProducts = async () => {
    const cacheKey = `products_page_${currentPage}_${JSON.stringify(category)}_${JSON.stringify(
      subCategory
    )}_${sortType}_${search}`;
    const cachedPage = sessionStorage.getItem(cacheKey);

    const cacheData = (data) => {
      // ✅ No need to shuffle anymore — backend has interleaved properly
      const products = data.products;

      sessionStorage.setItem(cacheKey, JSON.stringify({ ...data, products }));
      setProducts(products);
      setTotalPages(data.totalPages);
      setIsInitialLoadDone(true);
    };

    if (cachedPage) {
      try {
        const parsed = JSON.parse(cachedPage);
        setProducts(parsed.products);
        setTotalPages(parsed.totalPages);
        setIsInitialLoadDone(true);
        return;
      } catch (e) {
        console.error('Cache parse error, removing cache:', e);
        sessionStorage.removeItem(cacheKey);
      }
    } else {
      setIsLoading(true);
    }

    try {
      const params = {
        page: currentPage,
        limit: productsPerPage,
        sortField:
          sortType === 'low-high'
            ? 'price'
            : sortType === 'high-low'
            ? 'price'
            : sortType === 'rating'
            ? 'averageRating'
            : sortType === 'bestseller'
            ? 'bestseller'
            : sortType === 'latest'
            ? 'date'
            : 'relevance', // 'relevance' is backend interleaved now
        sortOrder: sortType === 'low-high' ? 1 : sortType === 'latest' ? -1 : -1,
      };

      if (category.length > 0) params.category = category[0];
      if (subCategory.length > 0) params.subCategory = subCategory[0];
      if (search) params.search = search;

      const res = await axios.get(`${backendUrl}/api/product/list`, { params });

      if (res.data.success) {
        cacheData(res.data);

        // ✅ Prefetch next page, no shuffle needed
        if (currentPage < res.data.totalPages) {
          const nextPageParams = { ...params, page: currentPage + 1 };
          const nextCacheKey = `products_page_${currentPage + 1}_${JSON.stringify(
            category
          )}_${JSON.stringify(subCategory)}_${sortType}_${search}`;

          axios
            .get(`${backendUrl}/api/product/list`, { params: nextPageParams })
            .then((nextRes) => {
              if (nextRes.data.success) {
                const nextData = { ...nextRes.data, products: nextRes.data.products };
                sessionStorage.setItem(nextCacheKey, JSON.stringify(nextData));
              }
            })
            .catch((err) => console.error('Prefetch next page failed:', err));
        }
      }
    } catch (err) {
      console.error('Failed to fetch products', err);
    } finally {
      if (!cachedPage) setIsLoading(false);
    }
  };

  const clearCachedProducts = () => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith('products_page_')) {
        sessionStorage.removeItem(key);
      }
    });
  };

  useEffect(() => {
    if (filtersReady) {
      fetchProducts();
    }
  }, [currentPage, category, subCategory, sortType, search, filtersReady]);

  // Reset to first page when search changes
  useEffect(() => {
    if (filtersReady) {
      setCurrentPage(1);
      sessionStorage.setItem('currentPage', '1');
    }
  }, [search]);

  const applyFilter = (resetPage = true) => {
    sessionStorage.setItem('categoryFilter', JSON.stringify(category));
    sessionStorage.setItem('subCategoryFilter', JSON.stringify(subCategory));
    sessionStorage.setItem('sortType', sortType);

    if (resetPage) {
      setCurrentPage(1);
      sessionStorage.setItem('currentPage', '1');
    }

    // 🚀 Trigger fetchProducts via useEffect
  };

  const safeJSONParse = (value, fallback = []) => {
    try {
      if (!value || value === 'undefined') return fallback;
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const paginate = () => {
    const startIndex = (currentPage - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    return filterProducts.slice(startIndex, endIndex);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    sessionStorage.setItem('currentPage', page);

    // Smooth scroll fix for iOS – with timeout ensures DOM render before scroll
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 50); // Slight delay helps stabilize scroll in iOS
  };

  const toggleCategory = (cat) => {
    setCategory((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  };

  const toggleSubCategory = (subCat) => {
    setSubCategory((prev) =>
      prev.includes(subCat) ? prev.filter((s) => s !== subCat) : [...prev, subCat]
    );
  };

  return (
    <div className="flex flex-col sm:flex-row px-4 sm:pt-28 pt-10  gap-1 sm:gap-10  border-t">
      <div className="pb-4 sm:hidden">
        <SearchBar />
      </div>
      {/* Mobile Filter Bar - Horizontal 3 Dropdowns */}
      <div className="sm:hidden w-full mb-5 mt-[-20px]">
        <div className="flex gap-1 w-full">
          {/* Category Dropdown */}
          <div className="flex-1 relative">
            <select
              value={category[0] || ''}
              onChange={(e) => {
                const value = e.target.value;
                setCategory(value ? [value] : []);
                setSubCategory([]);
                setCurrentPage(1);
              }}
              className="w-full min-w-[90px] border-2 border-[#4c0f2e] text-[#4c0f2e] bg-white rounded-full px-4 pr-8 py-[6px] text-sm font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-[#4c0f2e]/30 shadow-sm break-words"
            >
              <option value="">Filters</option>
              {Object.keys(categorySubCategoryMap).map((cat) => (
                <option key={cat} value={cat} className="break-words">
                  {cat}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <svg
                className="h-3 w-3 text-[#4c0f2e]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {/* Subcategory Dropdown */}
          <div className="flex-1 relative">
            <select
              value={subCategory[0] || ''}
              onChange={(e) => {
                const value = e.target.value;
                setSubCategory(value ? [value] : []);
                applyFilter(true);
              }}
              disabled={!category.length}
              className="w-full min-w-[110px] border-2 border-[#4c0f2e] text-[#4c0f2e] bg-white rounded-full px-4 pr-8 py-[6px] text-sm font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-[#4c0f2e]/30 shadow-sm break-words"
            >
              <option value="">Sub-Category</option>
              {category.length > 0 &&
                categorySubCategoryMap[category[0]].map((subCat) => (
                  <option key={subCat} value={subCat} className="break-words">
                    {subCat}
                  </option>
                ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <svg
                className="h-3 w-3 text-[#4c0f2e]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {/* Sort Dropdown */}
          <div className="flex-1  relative">
            <select
              value={sortType}
              onChange={(e) => {
                setSortType(e.target.value);
                applyFilter(true);
              }}
              className="w-full min-w-[110px] border-2 border-[#4c0f2e] text-[#4c0f2e] bg-white rounded-full px-4 pr-8 py-[6px] text-sm font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-[#4c0f2e]/30 shadow-sm"
            >
              <option value="relevant">Sort</option>
              <option value="low-high">Low → High</option>
              <option value="high-low">High → Low</option>
              <option value="rating">Top Rated</option>
              <option value="bestseller">Bestseller</option>
              <option value="latest">Latest</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
              <svg
                className="h-3 w-3 text-[#4c0f2e]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        <style>{`
  select option:hover,
  select option:checked {
    background-color: #FFDFD3 !important;
    color: #4c0f2e !important;
  }

  select {
    color: #4c0f2e;
    word-break: break-word !important;
  }
`}</style>
      </div>

      <div className="hidden sm:block w-1/3">
        <div className="sticky top-4 border border-[#D3756B]/20 p-6 rounded-xl shadow-md bg-white">
          <h2 className="text-2xl font-bold text-[#2a081a] mb-6">Filters</h2>

          {/* Category Section */}
          <div className="mb-8">
            <p className="text-lg font-semibold text-[#2a081a] mb-4">Category</p>
            <div className="space-y-3">
              {Object.keys(categorySubCategoryMap).map((cat) => (
                <label key={cat} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={category.includes(cat)}
                    onChange={() => toggleCategory(cat)}
                    className="w-5 h-5 rounded-md accent-[#2a081a] cursor-pointer"
                  />
                  <span className="text-md text-[#2a081a] group-hover:text-[#2a081a] transition-colors">
                    {cat}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Sub-Category Section */}
          <div className="mb-8">
            <p className="text-lg font-semibold text-[#2a081a] mb-4">Sub-Category</p>
            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#2a081a] scrollbar-track-gray-100">
              {category.length > 0 && Array.isArray(categorySubCategoryMap[category[0]]) ? (
                categorySubCategoryMap[category[0]].map((subCat) => (
                  <label key={subCat} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={subCategory.includes(subCat)}
                      onChange={() => toggleSubCategory(subCat)}
                      className="w-5 h-5 rounded-md accent-[#2a081a] cursor-pointer"
                    />
                    <span className="text-md text-[#2a081a] group-hover:text-[#2a081a] transition-colors">
                      {subCat}
                    </span>
                  </label>
                ))
              ) : (
                <p className="text-[#4c0f2e] italic text-base">Select a category first</p>
              )}
            </div>
          </div>

          {/* Apply Filter Button */}
          <button
            onClick={() => applyFilter(true)}
            className="w-full py-3 bg-[#2a081a] text-white text-lg font-semibold rounded-lg shadow-md 
                hover:bg-[#4c0f2e] active:scale-[0.98] transition-all duration-200"
          >
            Apply Filters
          </button>
        </div>
      </div>

      <div className="flex flex-col min-h-screen">
        <div className="hidden md:flex items-center mb-6 gap-4 w-full">
          {/* Search Bar */}
          <div className="flex-[6]">
            <SearchBar />
          </div>

          {/* Sort By */}
          <div className="flex-[1] flex font-forum items-center gap-2 whitespace-nowrap">
            <p className="text-md font-forum font-semibold text-[#9d4a54]">Sort By:</p>
            <select
              value={sortType}
              onChange={(e) => {
                setSortType(e.target.value);
                applyFilter(true);
              }}
              className="py-2 px-3 rounded-lg text-[#4c0f2e] border border-[#4c0f2e] text-sm"
            >
              <option value="relevant">Sort</option>
              <option value="low-high">Price: Low to High</option>
              <option value="high-low">Price: High to Low</option>
              <option value="rating">Rating</option>
              <option value="bestseller">Bestseller</option>
              <option value="latest">Latest</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 ">
            {[...Array(20)].map((_, index) => (
              <ProductItemSkeleton key={index} />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 col-span-full text-[#4c0f2e]">
            <p className="text-xl font-semibold">No results</p>
            <p className="text-md mt-2 text-[#4c0f2e]/80">No results found for your selection.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 ">
            {products.map((item, index) => (
              <ProductItem
                key={index}
                name={item.name}
                id={item._id}
                price={item.price}
                image={item.image}
                rating={item.averageRating || 0}
                totalReviews={item.totalReviews}
                bestseller={item.bestseller}
                category={item.category}
                subCategory={item.subCategory}
                description={item.description}
                inStock={item.inStock}
                sizes={item.sizes}
                stockQuantity={item.stockQuantity}
                currentPage={currentPage}
                categoryFilter={category}
                subCategoryFilter={subCategory}
                sortType={sortType}
                pageKey={'collection'}
                restock={item.restock}
              />
            ))}
          </div>
        )}

        {!isLoading && (
          <div ref={paginationRef} className="flex justify-center mt-6 items-center space-x-2">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => handlePageChange(index + 1)}
                className={`px-3 py-2 mx-1 rounded-md ${
                  currentPage === index + 1
                    ? 'bg-[#4c0f2e] text-[#ebe2db]'
                    : 'bg-[#ebe2db] text-[#4c0f2e]'
                }`}
                aria-label={`Go to page ${index + 1}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}

        {/* ✅ Place this outside return, at the end of your component */}
        <ScrollButton paginationRef={paginationRef} />
      </div>
    </div>
  );
};

export default Collection;
